import React, { useMemo, useState } from 'react';
import { useMapStore } from '../../store/useMapStore';

import { ChevronDown, ChevronUp, Fuel, Droplet } from 'lucide-react';
import { FUEL_BRANDS, FUEL_GROUPS } from '../../constants/fuel';
import type { FuelGroupId } from '../../types/gis';

const ACCENT = '#10b981';

export const FuelFiltersPanel: React.FC = () => {
  const fuelRadiusKm = useMapStore(state => state.fuelRadiusKm);
  const setFuelRadiusKm = useMapStore(state => state.setFuelRadiusKm);
  const fuelFilters = useMapStore(state => state.fuelFilters);
  const setFuelFilters = useMapStore(state => state.setFuelFilters);
  const fuelSummary = useMapStore(state => state.fuelSummary);
  const fuelManifest = useMapStore(state => state.fuelManifest);
  const theme = useMapStore(state => state.theme);

  const [isMinimized, setIsMinimized] = useState(false);
  const isDark = theme === 'dark';

  const handleToggleBrand = (brand: string) => {
    const current = fuelFilters.brands;
    setFuelFilters({
      brands: current.includes(brand) ? current.filter(b => b !== brand) : [...current, brand]
    });
  };

  const handleToggleGroup = (group: FuelGroupId) => {
    const current = fuelFilters.fuelGroups;
    setFuelFilters({
      fuelGroups: current.includes(group) ? current.filter(g => g !== group) : [...current, group]
    });
  };

  const { brandCounts, groupCounts } = useMemo(() => {
    if (fuelSummary) {
      return { brandCounts: fuelSummary.countsByBrand, groupCounts: fuelSummary.countsByFuelGroup };
    }
    if (fuelManifest) {
      return {
        brandCounts: fuelManifest.statewide.by_brand,
        groupCounts: fuelManifest.statewide.by_fuel_group
      };
    }
    return { brandCounts: {}, groupCounts: {} };
  }, [fuelSummary, fuelManifest]);

  const availableBrands = useMemo(() => {
    const known = new Set(fuelManifest?.brands ?? []);
    return FUEL_BRANDS.filter(b => known.size === 0 || known.has(b.id));
  }, [fuelManifest]);

  const scopeLabel = fuelSummary?.name ? `${fuelSummary.name} (${fuelRadiusKm}km)` : `Nearby Outlets (${fuelRadiusKm}km)`;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '8px',
      background: isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: '20px',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
      boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.2)',
      maxHeight: '70dvh',
      overflowY: 'auto',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }} className="custom-scrollbar fuel-filters-inner">

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 8px',
        borderBottom: isMinimized ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
        marginBottom: isMinimized ? 0 : '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Fuel size={16} color={ACCENT} />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{
              fontSize: '11px',
              fontWeight: 800,
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {scopeLabel}
            </span>
            {fuelSummary && (
              <span style={{ fontSize: '9px', opacity: 0.75, color: 'var(--text-secondary)' }}>
                {fuelSummary.total} {fuelSummary.total === 1 ? 'station' : 'stations'}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setIsMinimized(!isMinimized)}
          aria-expanded={!isMinimized}
          aria-label={isMinimized ? 'Expand fuel filters' : 'Minimize fuel filters'}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            padding: '4px',
            display: 'flex'
          }}
        >
          {isMinimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {!isMinimized && (
        <>
          {/* Radius Meter & Control */}
          <div style={{
            padding: '8px 12px',
            background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(16, 185, 129, 0.06)',
            borderRadius: '14px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(16, 185, 129, 0.12)'}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
                🎯 Search Radius
              </span>
              <span style={{ fontSize: '12px', fontWeight: 800, color: ACCENT }}>
                {fuelRadiusKm} km
              </span>
            </div>

            {/* Radius Quick Presets */}
            <div style={{ display: 'flex', gap: '4px', justifyContent: 'space-between' }}>
              {[2, 5, 10, 15, 20].map((r) => {
                const isActive = fuelRadiusKm === r;
                return (
                  <button
                    key={r}
                    onClick={() => setFuelRadiusKm(r)}
                    style={{
                      flex: 1,
                      padding: '4px 2px',
                      borderRadius: '8px',
                      border: `1px solid ${isActive ? ACCENT : 'transparent'}`,
                      background: isActive ? ACCENT : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                      color: isActive ? '#ffffff' : 'var(--text-primary)',
                      fontSize: '11px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    {r}km
                  </button>
                );
              })}
            </div>

            {/* Radius Range Slider */}
            <input
              type="range"
              min={1}
              max={25}
              step={1}
              value={fuelRadiusKm}
              onChange={(e) => setFuelRadiusKm(parseFloat(e.target.value))}
              style={{
                width: '100%',
                accentColor: ACCENT,
                cursor: 'pointer'
              }}
            />
          </div>

          <div style={{ height: '1px', background: 'var(--border-glass)', margin: '4px 16px' }} />

          {/* Brands */}
          <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Fuel size={14} color={ACCENT} />
              <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '1px' }}>
                Oil Companies
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {availableBrands.map(brand => {
                const isActive = fuelFilters.brands.includes(brand.id);
                const count = brandCounts[brand.id] || 0;
                return (
                  <button
                    key={brand.id}
                    onClick={() => handleToggleBrand(brand.id)}
                    aria-pressed={isActive}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      borderRadius: '20px',
                      border: `1px solid ${isActive ? brand.color : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')}`,
                      background: isActive ? `${brand.color}15` : 'transparent',
                      color: isActive ? brand.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: brand.color }} />
                    {brand.label}
                    <span style={{ opacity: 0.65, fontWeight: 700 }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Fuel groups */}
          <div style={{ padding: '0 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Droplet size={14} color={ACCENT} />
              <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '1px' }}>
                Fuel Available
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {FUEL_GROUPS.map(group => {
                const isActive = fuelFilters.fuelGroups.includes(group.id);
                const count = groupCounts[group.id] || 0;
                const isEmpty = count === 0 && !isActive;
                return (
                  <button
                    key={group.id}
                    onClick={() => handleToggleGroup(group.id)}
                    aria-pressed={isActive}
                    disabled={isEmpty}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      borderRadius: '20px',
                      border: `1px solid ${isActive ? ACCENT : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')}`,
                      background: isActive ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                      color: isActive ? ACCENT : 'var(--text-secondary)',
                      cursor: isEmpty ? 'not-allowed' : 'pointer',
                      opacity: isEmpty ? 0.4 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    {group.label}
                    <span style={{ opacity: 0.65, fontWeight: 700 }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
